import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import api from '../services/api';
import saucisseLogo from './saucisse.png';
import './CarteDeVisite.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

function generateTitles(achievements, genre) {
  const isFeminin = genre === 'F';

  // Titre 1 : meilleur succès de classement obtenu
  const achievementPriority = ['victory', 'top3', 'top8', 'top17', 'top33'];
  let title1 = isFeminin ? 'Participante' : 'Participant';

  for (const type of achievementPriority) {
    const earned = achievements.find(a => a.type === type && a.earned);
    if (earned) {
      title1 = earned.title;
      break;
    }
  }

  // Titre 2 : meilleur succès de participation (type='participation' uniquement, pas streak)
  let title2 = '';

  const participationAchievements = achievements
    .filter(a => a.type === 'participation' && a.earned)
    .sort((a, b) => b.threshold - a.threshold);

  if (participationAchievements.length > 0) {
    title2 = participationAchievements[0].title;
  }

  return { title1, title2 };
}

// Charge une image via le proxy et la dessine croppée (cover) dans un canvas
function cropImageToCanvas(canvasEl, imageUrl, size) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvasEl.width = size;
      canvasEl.height = size;
      const ctx = canvasEl.getContext('2d');
      // Calculer le crop "cover" centré
      const scale = Math.max(size / img.width, size / img.height);
      const sw = size / scale;
      const sh = size / scale;
      const sx = (img.width - sw) / 2;
      const sy = (img.height - sh) / 2;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
      resolve(true);
    };
    img.onerror = () => resolve(false);
    img.src = imageUrl;
  });
}

const CarteDeVisite = ({ smasheur, achievements, classements }) => {
  const cardRef = useRef(null);
  const avatarCanvasRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [avatarReady, setAvatarReady] = useState(false);
  const [charactersData, setCharactersData] = useState({});

  const proxyImageUrl = smasheur.profileImage
    ? `${API_URL}/proxy-image?url=${encodeURIComponent(smasheur.profileImage)}`
    : null;

  useEffect(() => {
    api.get('/characters').then(r => setCharactersData(r.data || {})).catch(() => {});
  }, []);

  // Pré-charger l'image de profil dans un canvas croppé
  useEffect(() => {
    if (proxyImageUrl && avatarCanvasRef.current) {
      cropImageToCanvas(avatarCanvasRef.current, proxyImageUrl, 240).then(ok => {
        setAvatarReady(ok);
      });
    }
  }, [proxyImageUrl]);

  const { title1, title2 } = generateTitles(achievements || [], smasheur.genre);
  const totalParticipations = (classements || []).length;
  const noParticipation = totalParticipations === 0;

  const characters = smasheur.personnages
    ? smasheur.personnages.split(',').map(name => name.trim()).filter(Boolean)
    : [];

  const getCharacterImageUrl = (charName) => {
    const charData = charactersData[charName];
    if (charData?.image) {
      return `${API_URL}/proxy-image?url=${encodeURIComponent(charData.image)}`;
    }
    return null;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await document.fonts.ready;

      // S'assurer que l'avatar est chargé
      if (proxyImageUrl && avatarCanvasRef.current && !avatarReady) {
        await cropImageToCanvas(avatarCanvasRef.current, proxyImageUrl, 240);
      }

      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });

      const link = document.createElement('a');
      link.download = `saucistats-${smasheur.pseudo}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {window.location.hostname === 'localhost' && (
        <button
          onClick={handleExport}
          disabled={exporting}
          className="btn btn-outline-warning btn-sm ms-3"
        >
          <i className="bi bi-download me-1"></i>
          {exporting ? 'Export en cours...' : 'Exporter la carte'}
        </button>
      )}

      {/* Carte off-screen pour capture */}
      <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
        <div ref={cardRef} className="carte-de-visite">
          <div className="carte-content">
            <div className="carte-avatar-section">
              {proxyImageUrl ? (
                <canvas
                  ref={avatarCanvasRef}
                  className="carte-avatar"
                  width={240}
                  height={240}
                />
              ) : (
                <div className="carte-avatar-fallback">
                  {smasheur.pseudo ? smasheur.pseudo.charAt(0).toUpperCase() : '?'}
                </div>
              )}
            </div>

            <h2 className="carte-pseudo">{smasheur.pseudo}</h2>

            {smasheur.tag && (
              <span className="carte-tag">{smasheur.tag}</span>
            )}

            {!noParticipation && (
              <div className="carte-titles">
                <div className="carte-title">
                  <span className="carte-title-icon">&#9733;</span>
                  {title1}
                </div>
                {title2 && (
                  <div className="carte-title">
                    <span className="carte-title-icon">&#9733;</span>
                    {title2}
                  </div>
                )}
              </div>
            )}

            {totalParticipations > 0 && (
              <div className="carte-participations">
                {totalParticipations} participation{totalParticipations > 1 ? 's' : ''}
              </div>
            )}

            {characters.length > 0 && (
              <>
                <div className="carte-separator"></div>
                <div className="carte-characters">
                  {characters.map((charName, idx) => {
                    const charImgUrl = getCharacterImageUrl(charName);
                    return (
                      <div key={idx} className="carte-character-item">
                        {charImgUrl ? (
                          <img
                            src={charImgUrl}
                            alt={charName}
                            className={`carte-character-img${noParticipation ? ' carte-character-img-large' : ''}`}
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className={`carte-character-fallback${noParticipation ? ' carte-character-fallback-large' : ''}`}>
                            <i className="bi bi-controller"></i>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="carte-bottom-bar">
            <img src={saucisseLogo} alt="Saucismash" className="carte-brand-logo" />
            {!noParticipation && (
              <span className="carte-points">{smasheur.points || 0} pts</span>
            )}
            <span className="carte-url">saucistats.com</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CarteDeVisite;
